import { window } from "global";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useUGGAssetsContext } from "@ugg/shared/components/UGGAssetsProvider";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { useLanguage } from "@ugg/shared/hooks/use-language-hooks";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { useAramMayhemPerChampionAugmentsByRarity, useAramMayhemAugmentManifest } from "@ugg/shared/hooks/aram-mayhem";
import { asArray } from "@ugg/shared/utils/safe-coerce";

interface AramMayhemSEOProps {
  className?: string;
  patch: string;
  displayPatch: string;
}

function buildAugmentBlurb(championName: string, augmentNames: string[], displayPatch: string): string {
  if (augmentNames.length < 3) {
    return `The best ${championName} ARAM Mayhem Build.`;
  }

  const [a, b, c] = augmentNames;
  return `The best ${championName} ARAM Mayhem Build. Top Augments for ${championName} are ${a}, ${b}, and ${c}, each are S+ picks when playing ARAM Mayhem.`;
}

function buildAugmentMetaDescription(championName: string, augmentNames: string[], displayPatch: string): string {
  if (augmentNames.length === 0) {
    return `Best ARAM Mayhem Augments for ${championName}. Learn S+ picks to win in ${displayPatch}.`;
  }

  if (augmentNames.length === 1) {
    return `Best ARAM Mayhem Augment for ${championName}: ${augmentNames[0]}. S+ pick to win in ${displayPatch}.`;
  }

  if (augmentNames.length === 2) {
    return `Best ARAM Mayhem Augments for ${championName}: ${augmentNames[0]} and ${augmentNames[1]}. S+ picks to win in ${displayPatch}.`;
  }

  const [a, b, c] = augmentNames;
  return `Best ARAM Mayhem Augments for ${championName}: ${a}, ${b}, ${c}. S+ picks to win in ${displayPatch}.`;
}

export default function AramMayhemSEO(props: AramMayhemSEOProps) {
  const { className, displayPatch, patch } = props;
  const { championId } = useChampionProfileContext();

  const { i18n } = useTranslation();
  const location = useLocation();

  const [lang] = useLanguage();

  const { staging } = useUGGAssetsContext();
  const envDir = staging ? "staging" : "prod";
  const { data: buildChampionNames } = useJSONFetcher(
    `https://static.bigbrain.gg/assets/lol/riot_patch_update/${envDir}/seo-champion-names.json`,
    { ssr: true },
  );

  const { data: perChampionAugmentsByRarity } = useAramMayhemPerChampionAugmentsByRarity({
    championKey: championId,
    patch,
    ssr: true,
  });
  const { data: augmentManifest } = useAramMayhemAugmentManifest({
    patch,
    ssr: true,
  });

  if (!window) {
    if (location.pathname.includes("/tr_tr")) {
      i18n.changeLanguage("tr_TR");
    }
  }

  if (!buildChampionNames) {
    return null;
  }

  const champion = buildChampionNames[championId];
  if (!champion) {
    return null;
  }
  const { name } = champion;

  const topPrismaticAugmentIds = asArray(perChampionAugmentsByRarity?.rarities?.kPrismatic);
  const topAugmentNames = topPrismaticAugmentIds
    .slice(0, 3)
    .map((id) => augmentManifest?.[String(id)])
    .filter((n): n is string => Boolean(n));

  const metaTitle = `${name} Mayhem Build, Augments, Items - Patch ${displayPatch}`;
  const metaDescription = buildAugmentMetaDescription(name, topAugmentNames, displayPatch);

  const blurb = buildAugmentBlurb(name, topAugmentNames, displayPatch);

  return (
    <div className={classNames(className)}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        <meta http-equiv="content-language" content={lang} />
        <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
      </Helmet>
      {blurb}
    </div>
  );
}
